/* ============================================================
   skon-pdp-live-price.js
   asset: assets/skon-pdp-live-price.js
   Aggiornato: 26 ottobre 2026 (Europe/Rome)
   Versione: 1.6.1 (badge PDP a tutta larghezza + didascalia sconto su una riga sola)

   Riscrive il price-block della buy-box PDP mostrando lo STACK degli sconti
   che quel visitatore ottiene davvero su quella variante:

     Prezzo al pubblico            32,00 €   (barrato, grigio)   ← compare_at
     Con promo attiva (-25%)       24,00 €   (barrato, grigio)   ← prezzo di vendita
     Il tuo Extra-Sconto (-30%)    16,80 €   (bold, verde)       ← codice sconto
     Risparmio totale -47%

   Ogni riga compare SOLO se esiste. Le percentuali sono sempre CALCOLATE, mai
   scritte a mano: la promo cambia per prodotto, l'extra viene dal
   discount-master Shopify.

   ── Modifiche v1.2.0 (24 ago 2026) ─────────────────────────────────────────
   - UNIFORMAZIONE COLLECTION ↔ PDP. Prima la PDP disegnava SOLO quando c'era
     un extra-sconto reale da codice (hasExtra). Sul semplice taglio-prezzi
     (compare_at > price SENZA codice) non toccava nulla: il price-block del
     tema mostrava barrato + prezzo, ma senza la PILL con la percentuale — che
     invece in collection compare sempre (pill "nativa"). Due contesti, due
     comportamenti.
     Ora: se non c'è extra-sconto MA c'è taglio-prezzi, la PDP disegna la
     STESSA riga-prezzo (barrato → verde → pill) SENZA la caption di
     scomposizione. Identica alla card di collection e allo stack col codice,
     solo senza la riga testuale sotto. La caption resta appannaggio del solo
     extra-sconto (dove c'è qualcosa da scomporre: "Promo X% + Extra Y%").
   - I due importi del taglio-prezzi si leggono in CENTESIMI dagli attributi
     data-skon-price / data-skon-compare-at emessi da snippets/price.liquid
     sullo stesso span.price già interrogato — nessun parsing di "€25,00",
     nessuna sonda di rete. Stessa fonte del modulo collection.
   - La percentuale del taglio-prezzi usa pctTotal (TRONCATA): è lo scarto
     complessivo dal prezzo al pubblico, l'affermazione più forte e
     verificabile. Mai dichiarare più sconto del reale.
   - BXGY / sconti posizionali: NON toccati da questa modifica. Un codice
     "ogni-secondo-articolo" sonda una variante singola → nessuna allocazione
     → hasExtra falso → cade sul ramo taglio-prezzi, che mostra il solo saldo
     di listino (corretto). La detection esplicita dei posizionali resta un
     passo separato, da affrontare in modo coerente con la collection.

   ── Modifiche v1.1.0 (9 lug 2026) ──────────────────────────────────────────
   - FIX CRITICO: lo sconto del codice non veniva MAI letto. Il vecchio codice
     faceva `if (finalAmt < origAmt) codeDiscountCents = finalAmt`, dove
     `origAmt` = compareAt e `finalAmt` = prezzo di vendita: con una promo
     manuale attiva la condizione è SEMPRE vera, e il ramo restituiva il prezzo
     di promo (24,00) spacciandolo per prezzo scontato. Il ramo
     `discountAllocations` — l'unico che contiene lo sconto vero del codice —
     stava nell'`else if` e non veniva mai raggiunto. Risultato visibile: 24,00
     barrato accanto a 24,00, più il compare_at del tema barrato: due barrati e
     nessuno sconto reale mostrato.
     Ora: `sconto codice = somma(discountAllocations)` SEMPRE; il prezzo finale
     è `amountPerQuantity - allocazioni`.
   - La riga "Il tuo Extra-Sconto" compare SE E SOLO SE Shopify alloca davvero
     uno sconto su quella variante (`discountAllocations > 0`). È la verifica
     live richiesta: se il prodotto è fuori dalla collection "• Prodotti
     Singoli", o il codice è esaurito, il motore sconti non alloca nulla e la
     riga non appare. Nessuna lista di handle da mantenere in parallelo.
   - Il `<s class="compare-at-price">` del tema viene NASCOSTO quando lo stack
     è attivo (la prima riga lo rimpiazza), e ripristinato dal reset.
   - Percentuale totale TRONCATA (47,5% → 47%): mai dichiarare uno sconto più
     alto di quello reale.
   - Aggiunti header e versione (il file non li aveva).

   DIPENDENZE: nessuna. Legge `/cart.js` e la Storefront API tokenless.
   NOTA: `cartCreate` costruisce un carrello EFFIMERO per sondare le regole
   sconto. Non tocca il carrello reale dell'utente.
   ============================================================ */

(function () {
  'use strict';

  var DISCOUNTED_SPAN_ID = 'skonLivePriceDiscounted';
  var LABEL_SPAN_ID      = 'skonLivePriceLabel';
  var STACK_ID           = 'skonLivePriceStack';
  var BADGE_ID           = 'skonPdpBadge';

  var COLOR_GREEN = '#1F5842';
  var COLOR_GREY  = '#6b6b6b';
  /* Badge Custom (1.4.0, 26 ott): se attivo nei settings, la pill (sfondo+testo)
     e il prezzo scontato prendono i colori custom, gli stessi del badge e della
     pill in collection. I colori arrivano da window.SKON_BADGE_CUSTOM, definito
     in theme.liquid dallo stesso checkbox. Solo presentazione; barrato e caption
     restano come sono. */
  function customBadge() {
    var c = window.SKON_BADGE_CUSTOM;
    return (c && c.enabled && c.bg) ? c : null;
  }

  /* v1.1.4 — `price.liquid` è renderizzato SIA dalla buy-box SIA dalle product
     card (righe 9-10 di quel file: "prodotti in PDP, product card, featured
     product e simili"). Su un PDP con prodotti correlati esistono quindi PIÙ
     <product-price> nel DOM, e `document.querySelector('product-price')`
     prende il PRIMO. Oggi la buy-box viene prima, quindi funziona — ma è una
     coincidenza di ordine DOM, non una garanzia: basta una card sopra la
     buy-box (upsell, "visti di recente") e nasconderemmo il prezzo di quella
     card, appendendole lo stack.
     La radice della buy-box è `.product-information` (product-information.liquid
     riga 116). Ancoriamo lì, con fallback al comportamento precedente. */
  function _buyBoxPrice() {
    return document.querySelector('.product-information product-price')
        || document.querySelector('product-price');
  }

  function formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /* Due percentuali, due affermazioni diverse.

     pctRule(): la % di una REGOLA nominale (la promo del prodotto, il codice
     sconto). Arrotondamento normale. Shopify tronca le allocazioni ai
     centesimi, quindi un codice al 30% su 12,02 € alloca 3,60 € = 29,95%:
     scriverlo "−29%" confonderebbe chi ha in mano un codice chiamato XTRA30.

     pctTotal(): il risparmio complessivo dichiarato sul prezzo al pubblico.
     È l'affermazione più forte e la più verificabile dal cliente. Dal 14 ott 2026
     (decisione di Matteo) usa ARROTONDAMENTO normale, come tutto il resto del sito. */
  function pctRule(fromCents, toCents) {
    if (!fromCents || fromCents <= 0 || toCents >= fromCents) return 0;
    return Math.round(((fromCents - toCents) / fromCents) * 100);
  }
  function pctTotal(fromCents, toCents) {
    if (!fromCents || fromCents <= 0 || toCents >= fromCents) return 0;
    return Math.round(((fromCents - toCents) / fromCents) * 100);
  }

  function getPriceCents() {
    var priceComp = _buyBoxPrice();
    if (!priceComp) return null;
    var span = priceComp.querySelector('.price');
    if (!span) return null;
    var text = span.textContent.trim();
    var num = parseFloat(text.replace(/[^\d,\.]/g, '').replace(',', '.'));
    return isNaN(num) || num === 0 ? null : Math.round(num * 100);
  }

  /* v1.2.0 — I due importi in CENTESIMI dal price.liquid (data-skon-price /
     data-skon-compare-at sullo span.price). Fonte esatta per il ramo
     taglio-prezzi: niente parsing di "€25,00", niente sonda. Ritorna null se
     l'attributo non c'è (es. compare-at assente = nessun taglio-prezzi). */
  function _attrCents(name) {
    var priceComp = _buyBoxPrice();
    if (!priceComp) return null;
    var span = priceComp.querySelector('.price');
    if (!span) return null;
    var raw = span.getAttribute(name);
    if (!raw) return null;
    var n = parseInt(raw, 10);
    return isNaN(n) || n <= 0 ? null : n;
  }

  function getVariantId() {
    var input = document.querySelector(
      'variant-select select, ' +
      '.product-form select[name="id"], ' +
      'input[name="id"][type="hidden"], ' +
      'input[name="id"]:not([type="hidden"])'
    );
    return input ? input.value : null;
  }

  function getShop() {
    return (window.Shopify && window.Shopify.shop) || location.hostname;
  }

  /* Ripristina il blocco prezzo allo stato originale */
  function resetPriceBlock() {
    var priceComp = _buyBoxPrice();
    if (!priceComp) return;
    var priceSpan = priceComp.querySelector('.price');
    if (priceSpan) {
      priceSpan.style.textDecoration = '';
      priceSpan.style.opacity = '';
      priceSpan.style.fontSize = '';
      priceSpan.style.display = '';
    }
    ['skonLivePriceDiscounted', 'skonLivePriceLabel', 'skonLivePriceStack', 'skonPdpBadge']
      .forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.remove();
      });
    clearBadgeShifts(priceComp.parentElement);
    /* Ripristina il compare-at del tema, nascosto quando lo stack è attivo */
    var themeCompare = priceComp.querySelector('s.compare-at-price');
    if (themeCompare) themeCompare.style.display = '';
  }

  /* ── Renderer (layout B, v1.1.1) ───────────────────────────────────────────
     Riga 1:  32,00 €  16,80 €  [−47%]
              barrato   verde     pill verde scuro
     Riga 2:  promo −25% + il tuo Extra-Sconto −30%   (didascalia)

     Il barrato eredita la dimensione del price block (come faceva il tema),
     il prezzo finale è grande e verde. La scomposizione scende in didascalia:
     i due numeri che contano restano leggibili a colpo d'occhio.

     Si disegna quando c'è un extra-sconto reale (con caption) OPPURE, dalla
     v1.2.0, quando c'è un semplice taglio-prezzi (senza caption: captionText
     vuoto → la riga didascalia non viene creata). In entrambi i casi la PILL
     e la riga-prezzo sono IDENTICHE: è ciò che uniforma PDP e collection. */
  /* Badge Custom in PDP (1.5.0, 26 ott): ricrea sopra il blocco prezzo lo stesso
     badge delle card (testo + countdown, colori custom), quando il Badge Custom
     e' attivo. Inline sopra <product-price> (non assoluto come sulla foto). Usa
     le classi gia' globali .skon-mini-badge / .skon-badge-promo / .skon-badge-
     countdown; i colori sono inline da window.SKON_BADGE_CUSTOM. Idempotente via
     id, rimosso in resetPriceBlock al cambio variante. Il countdown lo aggiorna
     il ticker globale (skon-badge-countdown.js). Solo presentazione. */
  /* Rimuove gli shift di grid-row applicati dal badge PDP. Idempotenza: va
     chiamata prima di ricalcolare e quando il badge non va mostrato, cosi' il
     prezzo non "scivola" di riga a ogni render. */
  function clearBadgeShifts(grid) {
    if (!grid) return;
    Array.prototype.forEach.call(grid.children, function (ch) {
      if (ch.getAttribute && ch.getAttribute('data-skon-shifted')) {
        ch.style.gridRow = '';
        ch.removeAttribute('data-skon-shifted');
      }
    });
  }

  function renderCustomBadge() {
    var priceComp = _buyBoxPrice();
    var grid = priceComp ? priceComp.parentElement : null;
    var old = document.getElementById(BADGE_ID);
    if (old) old.remove();
    clearBadgeShifts(grid);
    var cb = customBadge();
    if (!cb || !cb.label || !priceComp || !grid) return;

    var badge = document.createElement('div');
    badge.id = BADGE_ID;
    badge.className = 'skon-mini-badge';
    /* Versione PDP ("non-foto"): barra a TUTTA LARGHEZZA, UNA riga, angoli vivi
       (border-radius 0). font-size 11px inline perche' dentro la buy-box il badge
       erediterebbe il font grande del titolo-prezzo (h3, 20px). */
    var base = 'display:flex;align-items:center;gap:6px;'
      + 'background:' + cb.bg + ';color:' + cb.text + ';'
      + 'border-radius:0;padding:4px 10px;font-size:11px;line-height:1.2;'
      + 'font-weight:700;text-transform:uppercase;white-space:nowrap;';
    var lab = document.createElement('span');
    lab.className = 'skon-badge-promo';
    lab.textContent = (cb.emoji ? cb.emoji + ' ' : '') + cb.label;
    badge.appendChild(lab);
    if (cb.endsAt) {
      var sep = document.createElement('span');
      sep.textContent = '⏳';   // clessidra: separa testo e countdown sulla stessa riga
      badge.appendChild(sep);
      var cd = document.createElement('span');
      cd.className = 'skon-badge-countdown';
      cd.setAttribute('aria-hidden', 'true');
      badge.appendChild(cd);
    }

    /* Posizionamento tra Formato e la riga del prezzo, a TUTTA LARGHEZZA.
       La buy-box Horizon e' una griglia con placement esplicito (Formato riga 1,
       prezzo e disponibilita' riga 2). Metto il badge come item a span pieno
       (grid-column 1/-1) sulla riga del prezzo e spingo giu' di una riga gli item
       che erano li' (prezzo + disponibilita'), marcandoli data-skon-shifted.
       margin-bottom negativo (row-gap meno ~10px) per tenerlo attaccato al
       prezzo. Fallback (non e' una griglia): dentro product-price, in cima. */
    var isGrid = getComputedStyle(grid).display === 'grid';
    var priceRow = parseInt(getComputedStyle(priceComp).gridRowStart, 10);
    if (isGrid && priceRow >= 1) {
      Array.prototype.forEach.call(grid.children, function (ch) {
        if (ch === badge) return;
        var gr = parseInt(getComputedStyle(ch).gridRowStart, 10);
        if (!isNaN(gr) && gr >= priceRow) {
          ch.style.gridRow = String(gr + 1);
          ch.setAttribute('data-skon-shifted', '1');
        }
      });
      var rowGap = parseInt(getComputedStyle(grid).rowGap, 10) || 0;
      var pull = rowGap > 10 ? (rowGap - 10) : 0;
      badge.style.cssText = base + 'grid-column:1 / -1;grid-row:' + priceRow + ';'
        + 'align-self:start;margin:0 0 -' + pull + 'px 0;';
      grid.insertBefore(badge, priceComp);
    } else {
      badge.style.cssText = base + 'width:fit-content;margin:0 0 8px 0;';
      priceComp.insertBefore(badge, priceComp.firstChild);
    }
  }

  function renderPriceStack(strikeCents, finalCents, badgePct, captionText) {
    var priceComp = _buyBoxPrice();
    if (!priceComp) return;
    var container = priceComp.querySelector('[ref="priceContainer"]') || priceComp;

    var existing = document.getElementById(STACK_ID);
    if (existing) existing.remove();

    var priceSpan = container.querySelector('.price');
    if (priceSpan) priceSpan.style.display = 'none';
    var themeCompare = container.querySelector('s.compare-at-price');
    if (themeCompare) themeCompare.style.display = 'none';

    var stack = document.createElement('div');
    stack.id = STACK_ID;
    stack.style.cssText = 'display:block;';

    var line = document.createElement('div');
    line.style.cssText = 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;';

    var struck = document.createElement('span');
    struck.style.cssText = 'text-decoration:line-through;opacity:0.45;';
    struck.textContent = formatMoney(strikeCents);

    var final = document.createElement('span');
    var _cbF = customBadge();
    final.style.cssText = 'color:' + (_cbF ? _cbF.bg : COLOR_GREEN) + ';font-weight:700;';
    final.textContent = formatMoney(finalCents);

    line.appendChild(struck);
    line.appendChild(final);

    if (badgePct > 0) {
      var _cbp = customBadge();
      var pill = document.createElement('span');
      pill.style.cssText = 'display:inline-block;background:' + (_cbp ? _cbp.bg : COLOR_GREEN) + ';color:' + (_cbp ? _cbp.text : '#ffffff') + ';'
        + 'font-size:11px;font-weight:700;letter-spacing:0.04em;'
        + 'padding:3px 10px;border-radius:999px;line-height:1.4;white-space:nowrap;';
      pill.textContent = '\u2212' + badgePct + '%';
      if (_cbp && _cbp.emoji) pill.textContent = _cbp.emoji + ' ' + pill.textContent;
      line.appendChild(pill);
    }

    stack.appendChild(line);

    if (captionText) {
      var caption = document.createElement('span');
      /* nowrap: la didascalia ("Promo X% + il tuo Extra-Sconto Y%") e' larga ~293px,
         piu' della colonna prezzo (221px), quindi a capo si spezzava. Gli antenati
         hanno overflow visibile e la didascalia sta SOTTO la riga del prezzo (sotto
         "Disponibile"), quindi puo' sbordare a destra sulla stessa riga senza
         sovrapporsi a nulla (verificato dal vivo). */
      caption.style.cssText = 'display:block;color:' + COLOR_GREEN + ';opacity:0.6;font-size:10px;'
        + 'font-weight:600;letter-spacing:0.04em;text-transform:uppercase;white-space:nowrap;margin-top:3px;';
      caption.textContent = captionText;
      stack.appendChild(caption);
    }

    container.appendChild(stack);

    renderCustomBadge();
  }

  /* Percorso legacy: nessuno sconto reale allocato, ma twPromoConfig prevede
     una soglia. Mostra una PROIEZIONE, non uno sconto attivo — per questo il
     prezzo NON è in verde e la label dice cosa manca per ottenerlo. */
  function applyProjectedPrice(discountedCents, labelHTML) {
    var priceComp = _buyBoxPrice();
    if (!priceComp) return;
    var container = priceComp.querySelector('[ref="priceContainer"]') || priceComp;
    var priceSpan = container.querySelector('.price');
    if (!priceSpan) return;

    priceSpan.style.textDecoration = 'line-through';
    priceSpan.style.opacity = '0.45';

    var discounted = document.createElement('span');
    discounted.id = DISCOUNTED_SPAN_ID;
    discounted.style.cssText = 'margin-left:10px;color:' + COLOR_GREEN + ';font-weight:700;';
    discounted.textContent = formatMoney(discountedCents);
    priceSpan.insertAdjacentElement('afterend', discounted);

    if (labelHTML) {
      var label = document.createElement('span');
      label.id = LABEL_SPAN_ID;
      label.style.cssText = 'display:block;color:' + COLOR_GREEN + ';opacity:0.55;font-size:9px;'
        + 'font-weight:600;letter-spacing:0.05em;text-transform:uppercase;margin-top:2px;';
      label.innerHTML = labelHTML;
      discounted.insertAdjacentElement('afterend', label);
    }
  }

  async function updateLivePrice() {
    resetPriceBlock();

    var priceCents = getPriceCents();
    if (!priceCents) return;

    /* Legge carrello */
    var activeCodes = [];
    var cartTotal = 0;
    try {
      var r = await fetch('/cart.js', { credentials: 'include' });
      if (r.ok) {
        var cart = await r.json();
        cartTotal = cart.original_total_price || 0;
        /* v1.1.3 — NON filtrare su `applicable`. Shopify marca applicable:false
           quando il carrello REALE è vuoto (non ha righe su cui applicare lo
           sconto): con carrello vuoto il codice non arrivava alla sonda, la
           sonda non restituiva allocazioni e lo stack non compariva. Lo sconto
           si "accendeva" solo dopo il primo add-to-cart — cioè proprio dopo il
           momento in cui serviva a convincere.
           La sonda cartCreate costruisce un carrello CON DENTRO la variante ed
           è lei l'autorità: se il codice è scaduto, esaurito o non copre il
           prodotto, tornerà zero allocazioni da sola. Filtrare a monte su uno
           stato che dipende dal carrello reale impediva di rispondere alla
           domanda giusta: "se comprassi questo, quanto pagherei?". */
        activeCodes = (cart.discount_codes || []).map(function (d) { return d.code; });
      }
    } catch (e) {}

    /* twPromoConfig thresholds */
    var cfg = window.twPromoConfig || null;
    var thresholds = (cfg && cfg.thresholds)
      ? cfg.thresholds
          .filter(function (t) { return t.type === 'discount' || t.type === 'both'; })
          .sort(function (a, b) { return a.cents - b.cents; })
      : [];

    /* listCents  = prezzo al pubblico (compare_at), o il prezzo se non c'è
       promoCents = prezzo di vendita corrente (già scontato dalla promo)
       allocCents = sconto che Shopify alloca DAVVERO su questa variante */
    var listCents  = null;
    var promoCents = null;
    var allocCents = 0;

    /* Verifica codice sconto via Storefront API tokenless.
       cartCreate costruisce un carrello EFFIMERO: non tocca quello dell'utente. */
    if (activeCodes.length > 0) {
      var variantId = getVariantId();
      if (variantId) {
        try {
          var gid = 'gid://shopify/ProductVariant/' + variantId;
          var shop = getShop();
          var mutation = [
            'mutation cartCreate($input: CartInput!) {',
            '  cartCreate(input: $input) {',
            '    cart {',
            '      lines(first: 1) {',
            '        edges { node {',
            '          cost {',
            '            amountPerQuantity { amount }',
            '            compareAtAmountPerQuantity { amount }',
            '          }',
            '          discountAllocations { discountedAmount { amount } }',
            '        } }',
            '      }',
            '      discountCodes { code applicable }',
            '    }',
            '    userErrors { field message }',
            '  }',
            '}'
          ].join('\n');

          var sfResp = await fetch(
            'https://' + shop + '/api/2026-04/graphql.json',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: mutation,
                variables: {
                  input: {
                    lines: [{ merchandiseId: gid, quantity: 1 }],
                    discountCodes: activeCodes
                  }
                }
              })
            }
          );

          if (sfResp.ok) {
            var sfData = await sfResp.json();
            var sfCart = sfData.data && sfData.data.cartCreate && sfData.data.cartCreate.cart;
            var line   = sfCart && sfCart.lines.edges[0] && sfCart.lines.edges[0].node;
            if (line) {
              promoCents = Math.round(parseFloat(line.cost.amountPerQuantity.amount) * 100);
              listCents  = line.cost.compareAtAmountPerQuantity
                ? Math.round(parseFloat(line.cost.compareAtAmountPerQuantity.amount) * 100)
                : promoCents;

              /* v1.1.0 — LO SCONTO DEL CODICE È SEMPRE QUI, e solo qui.
                 Se il codice non copre questa variante (fuori dalla collection
                 "• Prodotti Singoli", esaurito, non combinabile), Shopify NON
                 alloca nulla e l'array è vuoto → nessuna riga Extra-Sconto.
                 È la verifica live, fatta dal motore che possiede le regole. */
              allocCents = (line.discountAllocations || []).reduce(function (sum, a) {
                return sum + Math.round(parseFloat(a.discountedAmount.amount) * 100);
              }, 0);
            }
          }
        } catch (e) {
          console.warn('[skon-live-price] sonda Storefront fallita:', e && e.message);
        }
      }
    }

    /* Se la sonda non ha risposto, ripieghiamo sul DOM: niente extra-sconto,
       ma lo stack promo resta corretto. */
    if (promoCents === null) promoCents = priceCents;
    if (listCents  === null) listCents  = promoCents;

    var finalCents = promoCents - allocCents;

    /* Lo stack si disegna SOLO se c'è un extra-sconto realmente allocato.
       Nessuna allocazione = il codice non copre questa variante (fuori dalla
       collection "• Prodotti Singoli", esaurito, non combinabile): il price
       block del tema resta com'è, e non promettiamo nulla che il checkout non
       applicherebbe. */
    var hasPromo = listCents > promoCents;
    var hasExtra = allocCents > 0;

    if (hasExtra) {
      var caption, badgePct;
      if (hasPromo) {
        /* v1.1.2 — niente segno meno davanti alle voci: il contesto è già di
           sconto, e le due percentuali si SOMMANO. Il meno resta solo nella
           pill, dove indica il risparmio complessivo sul prezzo al pubblico. */
        badgePct = pctTotal(listCents, finalCents);
        caption  = 'Promo ' + pctRule(listCents, promoCents) + '%'
                 + ' + il tuo Extra-Sconto ' + pctRule(promoCents, finalCents) + '%';
      } else {
        /* Nessuna promo: non c'è nulla da scomporre. Un'unica percentuale, e
           dev'essere quella della REGOLA (il codice XTRA30 dice 30). Se qui
           usassimo il totale troncato, la pill direbbe -29% mentre il codice
           dice 30: due numeri diversi accanto, sullo stesso prezzo. */
        badgePct = pctRule(promoCents, finalCents);
        caption  = '';
      }
      renderPriceStack(listCents, finalCents, badgePct, caption);
      return;
    }

    /* v1.2.0 — RAMO TAGLIO-PREZZI (nessun extra-sconto da codice).
       Se il prodotto è in saldo di listino (compare_at > price) mostriamo la
       STESSA riga-prezzo dello stack — barrato → verde → pill — ma SENZA
       caption: non c'è nulla da scomporre, è un solo sconto. È ciò che
       uniforma la PDP alla card di collection, dove la pill "nativa" compare
       sempre sui prodotti in saldo, anche senza codice.

       I due importi si leggono in centesimi dagli attributi di price.liquid
       (fonte esatta, no parsing di "€25,00", no sonda). La percentuale è
       pctTotal (troncata): lo scarto complessivo dal prezzo al pubblico. */
    var cutList  = _attrCents('data-skon-compare-at');
    var cutPrice = _attrCents('data-skon-price');
    if (cutList && cutPrice && cutList > cutPrice) {
      renderPriceStack(cutList, cutPrice, pctTotal(cutList, cutPrice), '');
      return;
    }

    /* Fallback twPromoConfig */
    if (thresholds.length > 0) {
      var highestThreshold = null;
      for (var i = thresholds.length - 1; i >= 0; i--) {
        if (thresholds[i].type === 'discount' || thresholds[i].type === 'both') {
          highestThreshold = thresholds[i];
          break;
        }
      }
      if (highestThreshold) {
        var unlockedThreshold = null;
        for (var j = thresholds.length - 1; j >= 0; j--) {
          if (cartTotal >= thresholds[j].cents) { unlockedThreshold = thresholds[j]; break; }
        }
        var targetThreshold = unlockedThreshold || highestThreshold;
        var discountedCents = Math.round(priceCents * (1 - targetThreshold.discount / 100));
        var condLabel = unlockedThreshold
          ? 'Con promo ora attiva'
          : 'Raggiungendo -' + targetThreshold.discount + '% in carrello';
        applyProjectedPrice(discountedCents, condLabel);
      }
    }
  }

  function init() {
    if (!_buyBoxPrice()) {
      var observer = new MutationObserver(function () {
        if (_buyBoxPrice()) {
          observer.disconnect();
          updateLivePrice();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { observer.disconnect(); }, 10000);
    } else {
      updateLivePrice();
    }

    document.addEventListener('variant:selected', function () { setTimeout(updateLivePrice, 150); });
    document.addEventListener('variant:update',   function () { setTimeout(updateLivePrice, 150); });
['cart:update', 'cart:refresh', 'cart:updated'].forEach(function (evt) {
      document.addEventListener(evt, function () { updateLivePrice(); });
    });

    /* Ricalcola alla chiusura del cart drawer e al cambio codice sconto */
    document.addEventListener('dialog:close', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('cart-drawer')) {
        updateLivePrice();
      }
    });
    document.addEventListener('discount:update', function () { updateLivePrice(); });
    /* v1.1.5 — Evento emesso da nc-experience.js quando riapplica il codice
       personale del quiz in una sessione nuova. Non usiamo `discount:update`
       perché lo ascolta anche il tema e con un detail non suo va in errore. */
    document.addEventListener('skon:discount-applied', function () { updateLivePrice(); });

    /* Fix Android Chrome: aggiunge classe selected sulla label al cambio variante */
    document.addEventListener('change', function(e) {
      var input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      var vp = input.closest('variant-picker');
      if (!vp) return;
      var fieldset = input.closest('fieldset');
      if (fieldset) {
        fieldset.querySelectorAll('label').forEach(function(l) {
          l.classList.remove('is-selected');
        });
      }
      var label = input.closest('label');
      if (label) label.classList.add('is-selected');
    });

    /* Applica is-selected alla variante pre-selezionata al caricamento */
    function applySelectedClass() {
      document.querySelectorAll('variant-picker input[type="radio"]:checked').forEach(function(input) {
        var label = input.closest('label');
        if (label) label.classList.add('is-selected');
      });
    }

    applySelectedClass();

    /* Riapplica dopo il morph del variant picker */
    document.addEventListener('variant:update', function() {
      setTimeout(applySelectedClass, 0);
    });
    document.addEventListener('variant:selected', function() {
      setTimeout(applySelectedClass, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();